import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { DEFAULT_SEO_PAGES } from "../utils/seoPages";

const Ctx = createContext(null);

const SETTINGS_CACHE_KEY = "sf_learning_site_settings_v1";
const LOGO = "https://res.cloudinary.com/drzrhdf7c/image/upload/v1780654890/sf-learning/brand/logo_sf_learning.png";

const FALLBACK = {
  site_name: "SF Learning",
  tagline: "L'ingénierie du Leadership",
  contact_email: "contact@sflearning.fr",
  whatsapp_number: "33664248707",

  founder_enabled: true,
  founder_image: "",
  founder_image_width: "w-80",
  founder_title: "Qui suis-je ?",
  founder_title_tag: "h2",
  founder_name: "Rudy Lemoine",
  founder_name_tag: "h3",
  founder_punchline: "Entrepreneur • Coach • Bâtisseur de systèmes",
  founder_punchline_tag: "p",
  founder_body_1: "Je m'appelle Rudy, et pendant des années j’ai vécu une vie qui ne me correspondait pas…",
  founder_body_1_tag: "p",
  founder_body_2: "Aujourd’hui, j’aide des hommes à reprendre le contrôle de leur vie, comprendre leurs blocages et créer une vie alignée avec qui ils sont vraiment.",
  founder_body_2_tag: "p",
  founder_cta_text: "Découvrir le programme",
  founder_cta_url: "/catalog",
  founder_facebook_url: "https://www.facebook.com/sflearningfr",
  founder_instagram_url: "https://www.instagram.com/sf.learning/",
  founder_youtube_url: "https://www.youtube.com/@RudyRevoLife",
  founder_linkedin_url: "https://www.linkedin.com/company/sf-learningfr",
  founder_tiktok_url: "https://www.tiktok.com/@rudyrevolife_officiel",

  hero_image: "",
  hero_image_position: "center center",
  hero_badge: "Plateforme officielle SF Learning",
  hero_title_line1: "Aligne.",
  hero_title_line2: "Structure.",
  hero_title_line3: "Scale.",
  hero_subtitle: "Des formations pensées pour transformer ton quotidien — mindset, productivité, sérénité.",
  hero_cta_primary: "Découvrir les formations",
  hero_cta_secondary: "Ton diagnostic stratégique offert !",
  hero_social_proof: "+1 000 apprenants accompagnés",

  stat_community_label: "Communauté active",
  stat_community_value: "+1 200 membres",
  stat_assignments_label: "Devoirs corrigés",
  stat_assignments_value: "⏱ moyenne 24h",

  feature1_title: "Formations en blocs",
  feature1_text: "Des leçons riches : texte, image, vidéo, YouTube, Vimeo, fichiers.",
  feature2_title: "Devoirs corrigés",
  feature2_text: "Tu rends ton travail, je corrige et te le retourne avec mes commentaires.",
  feature3_title: "Communauté bienveillante",
  feature3_text: "Échange, partage et grandis avec d'autres apprenants engagés.",

  catalog_label: "Nos formations",
  catalog_title_line1: "Des programmes pour",
  catalog_title_line2: "grandir",

  affiliate_label: "Programme de parrainage",
  affiliate_title_line1: "Recommande, partage,",
  affiliate_title_line2: "touche une commission",
  affiliate_subtitle: "Pas besoin d'être client pour gagner.",
  affiliate_cta: "Devenir affilié maintenant",
  affiliate_page_title: "Rejoins notre Programme Partenaires SF Learning",
  affiliate_page_subtitle: "Propulse l'ingénierie du leadership autour de toi. Recommande nos programmes de haute performance et génère 25% de commission sur chaque transformation.",
  affiliate_page_description: "Connecte-toi à ton compte apprenant pour accéder à ton code de parrainage, ton lien personnalisé et tes statistiques.",
  affiliate_page_cta: "Me connecter pour accéder à mon parrainage",
  affiliate_page_form_cta: "Obtenir mon lien & code",

  community_title: "Une communauté qui te ressemble",
  community_subtitle: "Échange avec d'autres apprenants, pose tes questions, partage tes victoires.",
  community_cta: "Rejoindre la communauté",

  google_analytics_id: "",
  google_search_console_verification: "",
  robots_txt: "User-agent: *\nAllow: /\nSitemap: {{ site_url }}/sitemap.xml",
  sitemap_enabled: true,
  seo_pages: DEFAULT_SEO_PAGES,
  zcal_booking_url: "https://zcal.co/rudylemoine/SFLearning",
  zcal_embed_enabled: true,
  abandoned_cart_enabled: true,
  abandoned_cart_delay_hours: 12,

  footer_description: "Plateforme de formations en ligne, accompagnements & communauté pour évoluer chaque jour.",
  footer_contact_text: "Une question ? Écris-nous, nous répondons sous 24h.",
  footer_copyright: "Tous droits réservés.",
};

function readCachedSettings() {
  try {
    const raw = window.localStorage.getItem(SETTINGS_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function cacheSettings(settings) {
  try {
    window.localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage can be unavailable in some browser modes. Ignore safely.
  }
}

function SettingsSplashScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f2fcee]">
      <div className="flex flex-col items-center gap-4 text-center">
        <img
          src={LOGO}
          alt="SF Learning"
          className="w-28 h-28 object-contain animate-pulse drop-shadow-lg"
        />
        <p className="font-heading text-sm font-semibold tracking-wide text-brand-green">
          Chargement de SF Learning...
        </p>
      </div>
    </div>
  );
}

export function SettingsProvider({ children }) {
  const cachedSettings = typeof window !== "undefined" ? readCachedSettings() : null;
  const [settings, setSettings] = useState({ ...FALLBACK, ...(cachedSettings || {}) });
  const [loadingSettings, setLoadingSettings] = useState(!cachedSettings);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      const nextSettings = { ...FALLBACK, ...data };
      setSettings(nextSettings);
      cacheSettings(nextSettings);
    } catch {
      // Keep cached settings if available; otherwise fallback.
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Ctx.Provider value={{ settings, loadingSettings, refresh: load, FALLBACK }}>
      {loadingSettings ? <SettingsSplashScreen /> : children}
    </Ctx.Provider>
  );
}

export const useSettings = () => useContext(Ctx) || { settings: FALLBACK, loadingSettings: false, refresh: () => {}, FALLBACK };
